import { useCallback, useEffect, useState } from 'react'
import { CreditCard, ExternalLink, Loader2, Phone } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/supabaseClient'
import BrdidAvulsoCheckout from '@/components/did/BrdidAvulsoCheckout'
import { cn } from '@/utils/cn'

const DOCS_URL = 'https://brdid.com.br/api-docs/#/did/adquirir_novo_did'

const STATUS_LABEL = {
  active: 'Ativo',
  grace: 'Carência',
  blocked: 'Bloqueado',
  canceled: 'Cancelado',
}

function formatBrl(cents) {
  if (cents == null) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    cents / 100
  )
}

function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso)
    )
  } catch {
    return iso
  }
}

export default function BrdidNumbersPanel() {
  const { getProfileId } = useAuth()
  const profileId = getProfileId()
  const [searchParams, setSearchParams] = useSearchParams()

  const [loadingOwned, setLoadingOwned] = useState(true)
  const [owned, setOwned] = useState([])

  const loadOwned = useCallback(async () => {
    if (!profileId) {
      setOwned([])
      setLoadingOwned(false)
      return
    }
    setLoadingOwned(true)
    const { data, error: qErr } = await supabase
      .from('whatsapp_numbers')
      .select('id, did_number, status, monthly_price_cents, next_charge_at, created_at')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false })

    if (qErr) {
      console.error('[BrdidNumbersPanel]', qErr)
      toast.error('Não foi possível carregar seus números.')
      setOwned([])
    } else {
      setOwned(data || [])
    }
    setLoadingOwned(false)
  }, [profileId])

  useEffect(() => {
    loadOwned()
  }, [loadOwned])

  useEffect(() => {
    const c = searchParams.get('checkout')
    if (c === 'success') {
      toast.success(
        'Pagamento confirmado. Os números podem levar alguns instantes para aparecer na lista.'
      )
      loadOwned()
      setSearchParams({}, { replace: true })
    } else if (c === 'canceled') {
      toast.error('Checkout cancelado.')
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, loadOwned, setSearchParams])

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Phone className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                Números (DID) BRDID
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Mesmo contrato do onboarding WappDesk: você pode adicionar quantos números quiser,
                cobrança mensal no cartão (Stripe). Após o pagamento, o DID é registrado na BRDID
                via API{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">adquirir_novo_did</code>.
              </p>
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Documentação BRDID
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" aria-hidden />
            <h3 className="text-base font-semibold text-foreground">Seus números contratados</h3>
          </div>
          <button
            type="button"
            onClick={() => loadOwned()}
            disabled={loadingOwned}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50 touch-manipulation"
          >
            Atualizar lista
          </button>
        </div>
        {loadingOwned ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando…
          </div>
        ) : owned.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum número BRDID vinculado a esta conta ainda. Contrate abaixo ou use o fluxo de
            contratação no WappDesk.
          </p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
            {owned.map((row) => (
              <li
                key={row.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-mono text-sm font-medium text-foreground">{row.did_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBrl(row.monthly_price_cents)}/mês · próx. cobrança{' '}
                    {formatDate(row.next_charge_at)}
                  </p>
                </div>
                <span
                  className={cn(
                    'inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-medium',
                    row.status === 'active' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
                    row.status === 'grace' && 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
                    (row.status === 'blocked' || row.status === 'canceled') &&
                      'bg-destructive/15 text-destructive'
                  )}
                >
                  {STATUS_LABEL[row.status] || row.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <BrdidAvulsoCheckout profileId={profileId} />
    </div>
  )
}
